import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import NotificationService, { NotificationProps } from 'services/hooks/notification-service';
import SelectEntity from '../Common/ListsEntity/SelectEntity';
import { useAuth } from 'services/auth-service';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import PrimaryButton from 'components/elements/Form/button/primaryButton';
import OrganizationService, { OrganizationProps } from 'services/hooks/organization-service';
import { toast } from 'react-toastify';
import {
  generateApiFailErrorMessage,
  generateBroadcastErrorMessage,
  generateBroadcastSuccessMessage
} from 'utils/helpers';
import BuildingService, { BuildingProps } from 'services/hooks/building-service';
import MultiSelectInput from 'components/elements/Form/select/multiSelectInput';

type MessageOccupantProps = {
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
};

/* The code is defining a validation schema using the `yup` library. If these validation rules are not met, an error message will be displayed. */
const CreateMsgOccupantsSchema = yup.object().shape({
  organizationUuid: yup.array().min(1, 'Organization is required')
});

export default function MessageOccupant({ setShowModal }: MessageOccupantProps) {
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    control
  } = useForm<NotificationProps>({
    defaultValues: {
      uuid: '',
      title: '',
      message: '',
      mobileUserUuid: '',
      buildingUuid: '',
      organizationUuid: []
    },
    resolver: yupResolver(CreateMsgOccupantsSchema)
  });
  const [orgOptions, setOrgOptions] = useState<{ value: string; label: string }[]>([]);
  const message = watch('message');
  const buildingWatch = watch('buildingUuid');

  const {
    state: { user }
  } = useAuth();

  const formatedOptions = (value: string, label: string) => {
    return { value: value, label: label };
  };

  // Query the `orgWatch` using getquery (GET request) by organization uuid
  const { data: buildingData } = useQuery<BuildingProps, Error>(
    ['building', buildingWatch, user.role === 'SUPER' || user.role === 'ACCOUNT'],
    () => BuildingService.findBuildingById(buildingWatch),
    {
      enabled: !!buildingWatch && (user.role === 'SUPER' || user.role === 'ACCOUNT'),
      onError: () => {
        toast.error(generateApiFailErrorMessage('Building'));
      }
    }
  );

  // Query the `orgWatch` using getquery (GET request) by organization uuid
  const { data: organzationsData } = useQuery<OrganizationProps[], Error>(
    ['organizations'],
    OrganizationService.findAllOrgs,
    {
      onError: () => {
        toast.error(generateApiFailErrorMessage('Organizations'));
      }
    }
  );

  // Populate Organization options based on selected Building
  useEffect(() => {
    // To populate Organization options
    if (buildingWatch) {
      const populateOrgOptions: { value: string; label: string }[] = [];
      buildingData?.organizations?.map((org: { uuid: string; name: string }) =>
        populateOrgOptions.push(formatedOptions(org?.uuid, org?.name))
      );
      setOrgOptions(populateOrgOptions);
    } else {
      const populateOrg: { value: string; label: string }[] = [];
      organzationsData?.map((org: { uuid: string; name: string }) =>
        populateOrg.push(formatedOptions(org?.uuid, org?.name))
      );

      setOrgOptions(populateOrg);
    }
  }, [buildingWatch, buildingData, organzationsData]);

  const { mutate } = useMutation(NotificationService.createBroadcast, {
    onSuccess: () => {
      setShowModal(false);
      queryClient.invalidateQueries('broadcast');
      toast.success(generateBroadcastSuccessMessage());
    },
    onError: () => {
      toast.error(generateBroadcastErrorMessage());
    }
  });

  const onSubmit = (data: NotificationProps) => {
    mutate(data);
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className={`text-left items-start w-full`}>
        <div className={'col-span-2 w-auto'}>
          {(user.role === 'SUPER' || user.role === 'ACCOUNT') && (
            <SelectEntity
              Controller={Controller}
              control={control}
              label="Choose a Building"
              placeholder="Select Building"
              queryname="buildings"
              name="buildingUuid"
              errors={errors}
              query={BuildingService.findAllBuildings}
              defaultValue=""
            />
          )}
          <MultiSelectInput
            Controller={Controller}
            control={control}
            name="organizationUuid"
            label="Choose Organizations"
            placeholder="Select Organizations"
            errors={errors}
            disabled={false}
            options={orgOptions}
          />
          <div className="col-span-2 w-full">
            <label className="block text-neutral7 font-bold" htmlFor="message">
              Message
            </label>
            <label className="block text-neutral7" htmlFor="message">
              Message is sent via SMS text message to all occupants within the selected
              organization.
            </label>
            <textarea
              {...register('message')}
              id="message"
              name="message"
              rows={5}
              style={{ borderColor: '#D3D3D3' }}
              className="w-full p-2 border rounded resize-vertical"
            />
            <div className="text-right text-sm text-neutral7">
              <span className={message?.length > 1400 ? 'text-secondary2' : ''}>
                {message?.length || 0}
              </span>{' '}
              / 1400
            </div>
          </div>
        </div>
        <PrimaryButton
          buttonLabel="Send message via SMS"
          size={`mt-4 px-6 w-auto gap-2 ${
            message?.length > 1400 || message?.length == 0 ? '!bg-neutral10 !text-neutral6' : ''
          }`}
          disabled={message?.length > 1400 || message?.length == 0}
        />
      </form>
    </>
  );
}
